import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { getPricing } from "../../services/api/app/credits";
import { fetchAccountBootstrap } from "../../services/api/app/accountBootstrap";
import { creditCostForTool } from "../../data/pricingModal";

function subscribedAudience(statusOrBootstrap) {
  const benefits = statusOrBootstrap?.benefits || statusOrBootstrap;
  const phase = benefits?.access_phase;
  const subscribed =
    benefits?.has_subscription ||
    phase === "active" ||
    phase === "grace";
  return subscribed ? "subscribed" : "payg";
}

/**
 * Backend-driven credit catalog for tools.
 * New tools: seed `tasks.task_name`, charge with consume_credits(task_name),
 * add a pricingModal row with the same `taskKey`, then call costFor(taskKey).
 */
export function usePricingCatalog() {
  const { getToken } = useAuth();
  const [packs, setPacks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tools, setTools] = useState([]);
  const [audience, setAudience] = useState("payg");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const [pricing, bootstrap] = await Promise.all([
          getPricing().catch(() => null),
          token
            ? fetchAccountBootstrap(token).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (pricing?.success) {
          setPacks(Array.isArray(pricing.packs) ? pricing.packs : []);
          setPlans(Array.isArray(pricing.plans) ? pricing.plans : []);
          setTools(Array.isArray(pricing.tools) ? pricing.tools : []);
        }
        setAudience(subscribedAudience(bootstrap));
      } catch {
        // keep static fallbacks in the caller
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const toolsByKey = useMemo(() => {
    const map = {};
    for (const tool of tools || []) {
      if (!tool?.key) continue;
      const prev = map[tool.key];
      const nextCost = Number(tool.credit_cost);
      const prevCost = Number(prev?.credit_cost);
      if (!prev || (Number.isFinite(nextCost) && nextCost > prevCost)) {
        map[tool.key] = tool;
      }
    }
    return map;
  }, [tools]);

  const costFor = useCallback(
    (taskKey) => creditCostForTool(toolsByKey[taskKey], audience),
    [toolsByKey, audience],
  );

  return { packs, plans, tools, toolsByKey, audience, loading, costFor };
}
